import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, DownloadSimple, Trash, CalendarBlank, Warning, Ticket, ChatCircleDots, X, LinkSimple } from "@phosphor-icons/react";
import { formatApiError } from "../../lib/api";
import MentionInput from "./MentionInput";

const STATUS = { open: ["Open", "bg-[#E0E7FF] text-[#3730A3]"], in_progress: ["In progress", "bg-[#FEF3C7] text-[#92400E]"],
  completed: ["Completed", "bg-[#DCFCE7] text-[#15803D]"], cancelled: ["Cancelled", "bg-[#F3F4F6] text-[#4B5563]"] };
const PRIO = { low: "text-[#6B7280]", normal: "text-[#1F3A57]", high: "text-[#B45309]", urgent: "text-[#B91C1C]" };
const TABS = [["mine", "My tasks"], ["all", "All staff tasks"], ["created", "Sent by me"], ["tickets", "My tickets"]];
const field = "h-9 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white";

const LinkChip = ({ link, customerHref }) => !link ? null : (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#3730A3] text-[10px]" data-testid="task-link-chip">
    <LinkSimple size={10} />{link.kind === "ticket" ? `Ticket ${link.ref}` : "Chat message"}
    {link.customer_name && <> · {customerHref && link.customer_id
      ? <a href={customerHref(link.customer_id)} className="underline" data-testid="task-link-customer">{link.customer_name}</a> : link.customer_name}</>}
  </span>
);

function NewTask({ client, onDone, prefill, clearPrefill }) {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState([]);
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (prefill) { setText(prefill.text); setPriority(prefill.priority || "normal"); } }, [prefill]);
  const submit = async () => {
    setBusy(true);
    try {
      await client.post("/crm-tasks", { text, assignee_ids: picked.map(p => p.id), due_date: due || null, priority,
        link: prefill?.link ? { kind: prefill.link.kind, id: prefill.link.id } : null });
      toast.success(picked.length ? `Task sent to ${picked.map(p => p.name).join(", ")}` : "Task added to your list");
      setText(""); setPicked([]); setDue(""); setPriority("normal"); clearPrefill?.(); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`bg-white border rounded-xl p-4 space-y-3 ${prefill ? "border-[#DC2626]" : "border-[#E5E2D9]"}`} data-testid="task-new-form">
      {prefill?.link && <div className="flex items-center gap-2"><span className="text-[11px] text-[#5C5A55]">Linked to</span><LinkChip link={prefill.link} />
        <button onClick={() => { clearPrefill(); setText(""); }} className="text-[#8A8780]" title="Remove link" data-testid="task-link-clear"><X size={12} /></button></div>}
      <MentionInput client={client} value={text} onChange={setText} picked={picked} setPicked={setPicked} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-[#5C5A55]">Assign to:</span>
        {picked.length ? picked.map(p => <span key={p.id} className="px-2 py-0.5 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px]" data-testid={`task-picked-${p.id}`}>@{p.name}</span>)
          : <span className="text-[11px] italic text-[#8A8780]">yourself (type @ to assign others)</span>}
        <label className="ml-auto inline-flex items-center gap-1 text-[11px] text-[#5C5A55]"><CalendarBlank size={14} />Due
          <input type="date" value={due} onChange={e => setDue(e.target.value)} className={field} data-testid="task-due-input" /></label>
        <select value={priority} onChange={e => setPriority(e.target.value)} className={field} data-testid="task-priority-select">
          {Object.keys(PRIO).map(p => <option key={p} value={p}>{p}</option>)}</select>
        <button onClick={submit} disabled={busy || text.trim().length < 2} data-testid="task-create-btn"
          className="h-9 px-4 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white text-xs font-semibold inline-flex items-center gap-1">
          <Plus size={14} weight="bold" />{busy ? "Sending…" : "Send task"}</button>
      </div>
    </div>
  );
}

function Comments({ t, client, onChange }) {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await client.post(`/crm-tasks/${t.id}/comments`, { text, mention_ids: picked.map(p => p.id) });
      if (picked.length) toast.success(`Added ${picked.map(p => p.name).join(", ")} to the task`);
      else toast.success("Reply posted");
      setText(""); setPicked([]); onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 pl-3 border-l-2 border-[#FEE2E2] space-y-2" data-testid={`task-comments-${t.id}`}>
      {(t.comments || []).map(c => (
        <div key={c.id} className="text-sm" data-testid={`task-comment-${c.id}`}>
          <div className="text-[10px] text-[#8A8780]"><b className="text-[#1A1A1A]">{c.by.name}</b> · {new Date(c.at).toLocaleString()}</div>
          <div className="whitespace-pre-wrap">{c.text}</div>
        </div>))}
      <div className="flex items-end gap-2">
        <div className="flex-1"><MentionInput client={client} value={text} onChange={setText} picked={picked} setPicked={setPicked} rows={2}
          testid={`task-comment-input-${t.id}`} placeholder="Reply… type @ to bring someone in (they're added to the task)" /></div>
        <button onClick={send} disabled={busy || !text.trim()} data-testid={`task-comment-send-${t.id}`}
          className="h-9 px-3 rounded-full bg-[#1A1A1A] text-white text-xs font-semibold disabled:opacity-50">{busy ? "…" : "Reply"}</button>
      </div>
    </div>
  );
}

function TaskRow({ t, client, onChange, customerHref }) {
  const [open, setOpen] = useState(false);
  const patch = async (body) => {
    try { await client.patch(`/crm-tasks/${t.id}`, body); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${t.task_no} permanently?`)) return;
    try { await client.delete(`/crm-tasks/${t.id}`); toast.success("Deleted"); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const last = t.history?.at(-1);
  return (
    <div className="py-3 flex flex-col md:flex-row md:items-start gap-2" data-testid={`task-row-${t.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[11px] text-[#8A8780]">
          <span className="font-mono">{t.task_no}</span><span className={`font-semibold uppercase ${PRIO[t.priority]}`}>{t.priority}</span>
          <span>from {t.created_by.name}</span><span>· {new Date(t.created_at).toLocaleDateString()}</span>
        </div>
        <div className={`text-sm mt-0.5 whitespace-pre-wrap ${t.status === "completed" ? "line-through text-[#8A8780]" : "text-[#1A1A1A]"}`}>{t.text}</div>
        <div className="mt-1 flex flex-wrap gap-1">{t.assignees.map(a => <span key={a.id} className="px-1.5 py-0.5 rounded bg-[#F7F6F2] text-[10px]">@{a.name}</span>)}
          {last && <span className="text-[10px] text-[#8A8780] ml-1">{last.action} by {last.by}{last.note ? ` · ${last.note}` : ""}</span>}</div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <LinkChip link={t.link} customerHref={customerHref} />
          <button onClick={() => setOpen(o => !o)} data-testid={`task-comments-toggle-${t.id}`}
            className="inline-flex items-center gap-1 text-[11px] text-[#B91C1C] font-semibold"><ChatCircleDots size={13} />
            {t.comments?.length ? `${t.comments.length} comment${t.comments.length > 1 ? "s" : ""}` : "Comment"}</button>
        </div>
        {open && <Comments t={t} client={client} onChange={onChange} />}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {t.overdue && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#B91C1C]" data-testid={`task-overdue-${t.id}`}><Warning size={12} />Overdue</span>}
        {t.can_update ? <>
          <input type="date" value={t.due_date || ""} onChange={e => patch({ due_date: e.target.value })} className={field} data-testid={`task-due-${t.id}`} />
          <select value={t.status} onChange={e => patch({ status: e.target.value })} className={field} data-testid={`task-status-${t.id}`}>
            {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}</select>
        </> : <>
          <span className="text-[11px]">{t.due_date ? `Due ${t.due_date}` : "No due date"}</span>
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${STATUS[t.status][1]}`}>{STATUS[t.status][0]}</span></>}
        {t.can_delete && <button onClick={del} title="Delete" data-testid={`task-delete-${t.id}`} className="text-[#B91C1C] p-1"><Trash size={16} /></button>}
      </div>
    </div>
  );
}

function Tickets({ client, ticketLink }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { client.get("/crm-tasks/my-tickets").then(r => setRows(r.data)).catch(() => setRows([])); }, [client]);
  if (!rows) return <div className="text-sm text-[#8A8780] p-4">Loading…</div>;
  if (!rows.length) return <div className="text-sm italic text-[#8A8780] p-4" data-testid="task-tickets-empty">No tickets assigned to you.</div>;
  return <div className="divide-y divide-[#F0EEE7]">{rows.map(t => (
    <div key={t.id} className="py-3 flex items-center gap-3" data-testid={`task-ticket-${t.id}`}>
      <Ticket size={16} className="text-[#DC2626] shrink-0" />
      <div className="flex-1 min-w-0"><div className="text-[11px] text-[#8A8780] font-mono">{t.ticket_no} · {t.customer_name || ""} · {new Date(t.created_at).toLocaleDateString()}</div>
        <div className="text-sm truncate">{t.issue}</div></div>
      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#F3F4F6] uppercase">{t.status}</span>
      {ticketLink && <a href={ticketLink} className="text-[11px] underline text-[#DC2626]">Open</a>}
    </div>))}</div>;
}

export default function TaskBoard({ client, ticketLink, onSave, prefill, clearPrefill, customerHref, hideAll = false }) {
  const [tab, setTab] = useState("mine");
  const [f, setF] = useState({ status: "", q: "", date_from: "", date_to: "" });
  const [data, setData] = useState({ tasks: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const params = Object.fromEntries(Object.entries({ scope: tab, ...f }).filter(([, v]) => v));
  const load = useCallback(() => {
    if (tab === "tickets") return;
    setLoading(true);
    client.get("/crm-tasks", { params }).then(r => setData(r.data)).catch(e => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [client, tab, f.status, f.q, f.date_from, f.date_to]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exp = async (fmt) => {
    try {
      const r = await client.get(`/crm-tasks/export.${fmt}`, { params, responseType: "blob" });
      onSave(r.data, `crm-tasks_${new Date().toISOString().slice(0, 10)}.${fmt}`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="task-board">
      <NewTask client={client} onDone={load} prefill={prefill} clearPrefill={clearPrefill} />
      <div className="flex flex-wrap items-center gap-2">
        {TABS.filter(([k]) => !(hideAll && k === "all")).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`task-tab-${k}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold border ${tab === k ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white border-[#E5E2D9]"}`}>
            {l}{k === "mine" && data.counts.mine_open ? ` · ${data.counts.mine_open}` : ""}</button>))}
        {data.counts.mine_overdue > 0 && <span className="text-[11px] font-bold text-[#B91C1C]" data-testid="task-overdue-count">{data.counts.mine_overdue} overdue</span>}
      </div>
      {tab === "tickets" ? <div className="bg-white border border-[#E5E2D9] rounded-xl px-4"><Tickets client={client} ticketLink={ticketLink} /></div> : <>
        <div className="flex flex-wrap items-center gap-2">
          <input value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="Search task, #, staff…" className={`${field} w-48`} data-testid="task-search" />
          <select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} className={field} data-testid="task-status-filter">
            <option value="">All statuses</option>{Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}<option value="overdue">Overdue</option></select>
          <label className="inline-flex items-center gap-1 text-[11px] text-[#5C5A55]">From
            <input type="date" value={f.date_from} onChange={e => setF({ ...f, date_from: e.target.value })} className={field} data-testid="task-from" /></label>
          <label className="inline-flex items-center gap-1 text-[11px] text-[#5C5A55]">To
            <input type="date" value={f.date_to} onChange={e => setF({ ...f, date_to: e.target.value })} className={field} data-testid="task-to" /></label>
          <div className="ml-auto flex gap-2">
            <button onClick={() => exp("csv")} data-testid="task-export-csv" className="h-9 px-3 border border-[#1A1A1A] rounded-md text-xs inline-flex items-center gap-1"><DownloadSimple size={12} />CSV</button>
            <button onClick={() => exp("pdf")} data-testid="task-export-pdf" className="h-9 px-3 border border-[#1A1A1A] rounded-md text-xs inline-flex items-center gap-1"><DownloadSimple size={12} />PDF</button>
          </div>
        </div>
        <div className={`bg-white border border-[#E5E2D9] rounded-xl px-4 divide-y divide-[#F0EEE7] ${loading ? "opacity-60" : ""}`} data-testid="task-list">
          {!data.tasks.length ? <div className="py-8 text-center text-sm italic text-[#8A8780]">No tasks here yet.</div>
            : data.tasks.map(t => <TaskRow key={t.id} t={t} client={client} onChange={load} customerHref={customerHref} />)}
        </div>
        <p className="text-[10px] text-[#8A8780]">Completed tasks can be deleted by a superadmin 30 days after completion, once they have been included in a CSV or PDF download.</p>
      </>}
    </div>
  );
}
